'use client';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getRedirectIfNeeded } from '@utils/orderLayout/redirectLogic';
import { Order, OrderStatus } from '@interfaces/order';
import { ReceiptState } from '@interfaces/receipts';
import { AppErrorState } from '@interfaces/appErrorsStatusCodes';
import { usePolling } from '@hooks/usePolling';
import { useOrderState } from './useOrderState';
import ErrorPage from '@shared/pages/ErrorPage';
import { defaultLocale } from '@i18n/index';
import { useProject } from '@context/ProjectContext';
import { getCurrentProject } from '@projectsConfig';

// const isFinalStatus = (status: OrderStatus) =>
//   [
//     'success',
//     'failed',
//     'cancelled_customer',
//     'cancelled_merchant',
//     'expired',
//     'create_error',
//   ].includes(status);

const isStartingStatus = (status: OrderStatus) =>
  [
    'created',
    'pending',
    'need_deposit',
    'deposit_confirmed',
    'recalculate',
    'order_pull_error',
    'need_retry_payment_details_attempt',
    'no_payment_details',
  ].includes(status);

interface PaymentContextType {
  order: Order;
  getOrder: () => Promise<any>;
  isPaymentSearchActive: boolean;
  areReceiptsUnderLimit: boolean;
  isAllReceiptsRejected: boolean;
  isUploadingAllowed: boolean;
  isReceiptDisabled: boolean;
  receiptState: ReceiptState;
}

export const PaymentContext = createContext<PaymentContextType | undefined>(
  undefined
);

export function usePayment() {
  const ctx = useContext(PaymentContext);
  if (!ctx) throw new Error('usePayment must be used within paymentContext');
  return ctx;
}

interface PaymentProviderProps {
  children: ReactNode;
  initialOrder: Order;
  initialReceiptState: ReceiptState;
}

export function PaymentProvider({
  children,
  initialOrder,
  initialReceiptState,
}: PaymentProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { projectId } = useProject();

  const [appErrorState, setAppErrorState] = useState<AppErrorState | null>(
    null
  );

  const {
    order,
    receiptState,
    getOrder,
    isPaymentSearchActive,
    areReceiptsUnderLimit,
    isAllReceiptsRejected,
    isUploadingAllowed,
    isReceiptDisabled,
  } = useOrderState({
    initialOrder,
    initialReceiptState,
    onError: setAppErrorState,
  });

  const shouldPoll =
    !!order.id && (isStartingStatus(order.status) || isPaymentSearchActive);
  usePolling(getOrder, 5000, shouldPoll && !appErrorState?.showErrorPage);

  useEffect(() => {
    if (!pathname) return;

    const localeMatch = pathname.match(/^\/([a-z]{2})(\/|$)/);
    const locale = localeMatch ? localeMatch[1] : defaultLocale;

    const redirectAction = getRedirectIfNeeded(
      order,
      receiptState,
      pathname,
      locale,
      getCurrentProject(projectId).receiptUpload
    );

    if (redirectAction.redirectTo) {
      navigate(redirectAction.redirectTo);
    }
  }, [
    order.status,
    order.id,
    pathname,
    isPaymentSearchActive,
    isUploadingAllowed,
  ]);

  if (appErrorState?.showErrorPage) {
    return (
      <ErrorPage
        statusCode={appErrorState.errorCode}
        redirectUrl={appErrorState.errorRedirectUrl}
      />
    );
  }

  return (
    <PaymentContext.Provider
      value={{
        order,
        getOrder,
        isPaymentSearchActive,
        areReceiptsUnderLimit,
        isAllReceiptsRejected,
        isUploadingAllowed,
        receiptState,
        isReceiptDisabled,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
}
